import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'utils/hooks';

import rooms from 'domains/wall_preview_rooms';

import Button from 'components/shared/Button';
import Modal from 'components/shared/Modal';
import EditRoomUpload from './EditRoomUpload';

import type { FeetInchesPair } from 'utils/math_util';

type EditRoomUploadsModalProps = {
  lastFocusedElementId: string;
  handleChangeRoom: () => void;
  onClose: () => void;
};

function EditRoomUploadsModal(props: EditRoomUploadsModalProps) {
  const { handleChangeRoom, lastFocusedElementId, onClose } = props;

  const dispatch = useAppDispatch();
  const drafts = useAppSelector(rooms.selectors.draftRooms);

  const [shouldUpdateWidth, setShouldUpdateWidth] = useState(false);
  const [currentWidthDisplay, setCurrentWidthDisplay] = useState<FeetInchesPair>([0, 0]);
  const [updatedWidthDisplay, setUpdatedWidthDisplay] = useState<FeetInchesPair>([0, 0]);

  const multipleRooms = drafts.length > 1;

  // Save any updated room data and merge response with existing
  function handleUpdateRooms() {
    dispatch(rooms.actions.saveUserRooms.pending());
    handleChangeRoom();
  }

  // Clear edits and send back to choose room modal
  function handleCancelEdit() {
    dispatch(rooms.actions.resetEditDrafts());
    onClose();
  }

  function updateCalculatedWidthDisplay(
    shouldUpdate: boolean,
    currentWidth: FeetInchesPair,
    updatedWidth: FeetInchesPair
  ) {
    setShouldUpdateWidth(shouldUpdate);
    setCurrentWidthDisplay(currentWidth);
    setUpdatedWidthDisplay(updatedWidth);
  }

  const primaryFooterGroup = (
    <React.Fragment>
      <Button type="confirm" onClick={handleUpdateRooms}>
        Save {multipleRooms ? 'all' : ''} with new measurements
      </Button>
      <Button type="cancel" onClick={handleCancelEdit}>
        Cancel
      </Button>
    </React.Fragment>
  );

  const secondaryFooterGroup = (
    <React.Fragment>
      <FontAwesomeIcon icon="ruler" size="lg" />
      <span>
        Saved width of space:{' '}
        <span className="width-label">
          {currentWidthDisplay[0]}ft {currentWidthDisplay[1]}in
        </span>
      </span>
      {shouldUpdateWidth && <span className="separator">|</span>}
      {shouldUpdateWidth && (
        <span className="new-width">
          New width:{' '}
          <span className="width-label">
            {updatedWidthDisplay[0]}ft {updatedWidthDisplay[1]}in
          </span>
        </span>
      )}
    </React.Fragment>
  );

  const roomUploads = useMemo(
    () =>
      drafts.map((draft) => (
        <EditRoomUpload
          key={draft.id}
          room={draft}
          handleChangeRoom={handleChangeRoom}
          updateCalculatedWidthDisplay={updateCalculatedWidthDisplay}
        />
      )),
    [handleChangeRoom, drafts]
  );

  return (
    <Modal
      title="Information about your room"
      labelledBy="edit-room-upload-modal"
      modalClass={['edit-room-upload-modal']}
      lastFocusedElementId={lastFocusedElementId}
      onClose={onClose}
      primaryFooterGroup={primaryFooterGroup}
      secondaryFooterGroup={secondaryFooterGroup}
      // if only one room upload, don't use paginated modal
      pageStartIndex={multipleRooms ? 0 : null}
    >
      {roomUploads}
    </Modal>
  );
}

export default EditRoomUploadsModal;
